import { errors } from 'lib_ui-primitives';
import genericRule from '../../namespace/relation/doingValidate_namespace_relation';

export default {
    ...genericRule,
    excludedNamespaceRelations: [],
    namespace: 'mqtt',
    relation: 'users',
    // The generic rule defines no priority, so deriving one from it yielded NaN - and the sorter
    // (rulesengine.io RulesArrayRepository) reads priority as `priority || 0`, which silently turned
    // that NaN back into 0, the generic rule's own effective priority. Ordering then fell to array
    // position rather than intent. State the number outright: higher runs first, generic is 0.
    priority: 10,
    description: 'Keep MQTT user titles out of the reserved Continuous Scan namespace',
    logic: doingValidate
};

/**
 * MQTT usernames are one namespace shared by two credential systems.
 *
 * Provisioned clients (fixed readers, integrations) authenticate with their `mqtt.users` title
 * and a bcrypt secret. Continuous Scan authenticates with an Auth0 access token, and its username
 * lives under a reserved prefix so the two can never be confused (CS-002).
 *
 * silo_security relies on that prefix being unavailable to provisioned records: it routes any
 * `cs_<tenantId>` username straight to the Auth0 path without a database lookup. A stored record
 * in that namespace would make the routing ambiguous again, which is the shape of #1715 - there,
 * Continuous Scan had claimed the bare tenant ID that readers were already titled with, and every
 * Continuous Scan client on such a tenant was denied at CONNECT.
 *
 * Note this deliberately does NOT reject a bare tenant ID as a title. Readers are routinely titled
 * with the tenant they serve because that title is substituted into their authorization topic
 * templates as the tenant routing key, and that convention long predates Continuous Scan.
 *
 * This rule is a usability guard, not the security boundary. It only runs for writes that go
 * through the UI rules engine, so a record could still reach `mqtt.users` by another route - the
 * customer-workflow rules that upsert an integration user straight to Mongo carry their own copy of
 * this check, in lib_workflow-rules/src/rules/rulesEngine/customerWorkflow/reservedUsername.ts. The
 * boundary is silo_security, which routes the whole `cs_` namespace to Auth0 without a lookup -
 * so a record stored there fails closed (it can never authenticate) rather than shadowing
 * anything. Catching it here just turns that silent dead end into an error at the point of entry.
 *
 * Keep in step with CONTINUOUS_SCAN_USERNAME_PREFIX in lib_ui-services/src/mqttCredentials.js, which
 * owns this contract. qa/mqtt-workflow-harness's mqttUsernamePrefix.unit.js reads the literal out of
 * all six sources and fails if any of them diverges.
 */
const RESERVED_USERNAME_PREFIX = 'cs_';

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * @param {{
 *   data: {newRecord:{title:string}};
 *   prerequisiteResults: Array<{result:Array<object>}>;
 *   context: Context;
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} message
 */
function doingValidate(message) {
    const { title } = message.data.newRecord;
    if (typeof title === 'string' && title.trim().toLowerCase().startsWith(RESERVED_USERNAME_PREFIX)) {
        throw new errors.ValidationError(
            `An MQTT user name may not start with "${RESERVED_USERNAME_PREFIX}" - that prefix is reserved for Continuous Scan.`,
            {
                title: 'Choose a different name, for example i11n_<name>_<tenant suffix>.'
            }
        );
    }
    return message;
}
